<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleClose">
    <div class="modal-content" @click.stop>
      <h2 class="modal-title">Cancel Subscription</h2>
      <div class="line-divider"></div>

      <div class="modal-body">
        <p class="confirmation-message">
          Are you sure you want to cancel your
          <strong>{{ planName }}</strong> subscription?
        </p>

        <div class="warning-section">
          <p class="warning-title">What happens when you cancel:</p>
          <ul class="warning-list">
            <li>
              Your subscription will remain active until the end of the current
              billing period
            </li>
            <li>
              You'll lose access to subscription features after the period ends
            </li>
            <li>Unused minutes will not roll over</li>
            <li>You can resubscribe anytime</li>
          </ul>
        </div>
      </div>

      <div class="button-group">
        <button
          class="confirm-btn"
          @click="handleConfirm"
          :disabled="isLoading"
        >
          <div v-if="isLoading" class="button-content">
            <div class="spinner"></div>
            <span>Cancelling...</span>
          </div>
          <span v-else>Yes, Cancel Subscription</span>
        </button>
        <button class="cancel-btn" @click="handleClose" :disabled="isLoading">
          No, Keep Subscription
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";

export default {
  name: "CancelSubscriptionModal",
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    planName: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    modalWidth() {
      return isMobile() ? "90%" : "500px";
    },
  },
  methods: {
    handleClose() {
      if (!this.isLoading) {
        this.$emit("close");
      }
    },
    handleConfirm() {
      this.$emit("confirm");
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 1.5rem 0 1.5rem;
  color: #111827;
}

.line-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 1.5rem;
}

.modal-body {
  padding: 0 1.5rem;
}

.confirmation-message {
  font-size: 1rem;
  color: #374151;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.confirmation-message strong {
  color: #111827;
  font-weight: 600;
}

.warning-section {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.warning-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 0.75rem;
}

.warning-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.warning-list li {
  font-size: 0.875rem;
  color: #7f1d1d;
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
  position: relative;
  line-height: 1.5;
}

.warning-list li:last-child {
  margin-bottom: 0;
}

.warning-list li::before {
  content: "•";
  position: absolute;
  left: 0.5rem;
  color: #ef4444;
  font-weight: bold;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.confirm-btn,
.cancel-btn {
  width: 100%;
  padding: 0.875rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-btn {
  background-color: #ef4444;
  color: white;
}

.confirm-btn:hover:not(:disabled) {
  background-color: #dc2626;
}

.cancel-btn {
  background-color: #f3f4f6;
  color: #374151;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.confirm-btn:disabled,
.cancel-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .modal-content {
    width: 95%;
  }

  .modal-title {
    font-size: 1.25rem;
    margin: 1.25rem 1.25rem 0 1.25rem;
  }

  .line-divider {
    margin: 0.75rem 1.25rem;
  }

  .modal-body {
    padding: 0 1.25rem;
  }

  .button-group {
    padding: 0 1.25rem 1.25rem 1.25rem;
  }
}
</style>
